<template>
  <div class="gl-addons-page">

    <!-- Page header -->
    <div class="container-fluid">
      <div class="gl-branch-header">
        <div>
          <div class="gl-branch-breadcrumb">
            <router-link to="/">Home</router-link>
            <span class="gl-bc-sep">/</span>
            <span>Add-ons</span>
          </div>
          <h1 class="gl-branch-title">Add-ons</h1>
          <p class="gl-branch-sub">Reusable across menus. Turning one off here takes it out of stock everywhere — to switch it off for just one menu, edit the add-on.</p>
        </div>
        <router-link to="/owner/addons/create" class="gl-add-btn gl-header-add-btn">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          Add Add-on
        </router-link>
      </div>
    </div>

    <div class="container-fluid">

      <!-- Stats strip -->
      <div class="gl-stats-strip">
        <div class="gl-stat-chip">
          <span class="gl-stat-num">{{ addons.length }}</span>
          <span class="gl-stat-lbl">Total</span>
        </div>
        <div class="gl-stat-divider"></div>
        <div class="gl-stat-chip">
          <span class="gl-stat-num gl-stat-active">{{ availableCount }}</span>
          <span class="gl-stat-lbl">In stock</span>
        </div>
        <div class="gl-stat-divider"></div>
        <div class="gl-stat-chip">
          <span class="gl-stat-num gl-stat-inactive">{{ addons.length - availableCount }}</span>
          <span class="gl-stat-lbl">Out of stock</span>
        </div>
        <div class="gl-stat-spacer"></div>
        <div class="gl-search-wrap">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
          <input v-model="search" type="text" class="gl-search-input" placeholder="Search add-ons…">
        </div>
      </div>

      <!-- ── SKELETON ── -->
      <template v-if="loading">
        <div class="gl-table-wrap">
          <div v-for="i in 6" :key="i" class="gl-skel-row" :style="`animation-delay:${i*40}ms`">
            <div class="gl-skel-line w30"></div>
            <div class="gl-skel-line w15"></div>
            <div class="gl-skel-line w15"></div>
            <div class="gl-skel-line w10"></div>
          </div>
        </div>
      </template>

      <!-- ── EMPTY ── -->
      <div v-else-if="filteredAddons.length === 0" class="gl-empty">
        <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"><path d="M12 2l2.4 7.4H22l-6 4.6 2.3 7.4L12 17l-6.3 4.4L8 14 2 9.4h7.6z"/></svg>
        <p>{{ search ? 'No add-ons match your search.' : 'No add-ons yet.' }}</p>
        <router-link v-if="!search" to="/owner/addons/create" class="gl-add-btn" style="font-size:13px;padding:8px 16px">Create your first add-on</router-link>
      </div>

      <!-- ── TABLE ── -->
      <template v-else>
        <div class="gl-table-wrap">
          <table class="gl-table">
            <thead>
              <tr>
                <th>Add-on</th>
                <th>SKU</th>
                <th>Group</th>
                <th class="text-right">Price</th>
                <th class="text-center">Used in</th>
                <th class="text-center">In stock</th>
                <th class="text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="addon in filteredAddons" :key="addon.id" class="gl-tbl-row">
                <td>
                  <div class="gl-tbl-addon-name">{{ addon.name }}</div>
                </td>
                <td>
                  <span v-if="addon.sku" class="gl-sku">{{ addon.sku }}</span>
                  <span v-else class="gl-muted">—</span>
                </td>
                <td>
                  <span v-if="addon.group_label" class="gl-group-pill">{{ addon.group_label }}</span>
                  <span v-else class="gl-muted">—</span>
                </td>
                <td class="text-right">
                  <span class="gl-price">+RM{{ formatPrice(addon.price) }}</span>
                </td>
                <td class="text-center">
                  <span class="gl-used-count">{{ addon.products_count || 0 }}</span>
                </td>
                <td class="text-center">
                  <button
                    type="button"
                    :class="['gl-toggle', { on: addon.is_available }]"
                    :disabled="togglingId === addon.id"
                    @click="toggleAvailability(addon)"
                    :title="addon.is_available ? 'In stock — click to mark out of stock' : 'Out of stock — click to mark in stock'"
                  >
                    <span class="gl-toggle-knob"></span>
                  </button>
                </td>
                <td>
                  <div class="gl-tbl-actions">
                    <router-link :to="`/owner/addons/${addon.id}/edit`" class="gl-tbl-btn-edit" title="Edit">
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                    </router-link>
                    <button @click="confirmDelete(addon)" class="gl-tbl-btn-delete" title="Delete">
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/></svg>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </template>

    </div>

    <!-- ── Mobile FAB ── -->
    <router-link to="/owner/addons/create" class="gl-mobile-fab">
      <span class="gl-fab-inner">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
        Add Add-on
      </span>
    </router-link>

    <!-- ── Delete Modal ── -->
    <div v-if="showDeleteModal" class="gl-modal-backdrop" @click.self="showDeleteModal = false">
      <div class="gl-modal">
        <div class="gl-modal-icon gl-modal-icon-danger">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>
        </div>
        <h2 class="gl-modal-title">Delete Add-on</h2>
        <p class="gl-modal-body">
          Are you sure you want to delete <strong>{{ selectedAddon?.name }}</strong>?
        </p>
        <div v-if="selectedAddon?.products_count > 0" class="gl-modal-blocked">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
          <span>This add-on is used in <strong>{{ selectedAddon.products_count }}</strong> menu item(s). Deleting it removes it from all of them.</span>
        </div>
        <div class="gl-modal-actions">
          <button class="gl-modal-cancel" @click="showDeleteModal = false">Cancel</button>
          <button class="gl-modal-confirm" @click="deleteAddon" :disabled="deleting">
            <span v-if="deleting" class="spinner-border spinner-border-sm" style="width:13px;height:13px;border-width:2px"></span>
            <span v-else>Delete</span>
          </button>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import { ownerAddonApi } from '@/services/ownerApi';

export default {
  name: 'AddonList',

  data() {
    return {
      loading: true,
      deleting: false,
      togglingId: null,
      showDeleteModal: false,
      search: '',
      addons: [],
      selectedAddon: null
    };
  },

  computed: {
    availableCount() {
      return this.addons.filter(a => a.is_available).length;
    },
    filteredAddons() {
      if (!this.search.trim()) return this.addons;
      const q = this.search.trim().toLowerCase();
      return this.addons.filter(a =>
        a.name.toLowerCase().includes(q) ||
        (a.sku || '').toLowerCase().includes(q) ||
        (a.group_label || '').toLowerCase().includes(q)
      );
    }
  },

  mounted() {
    this.fetchAddons();
  },

  methods: {
    formatPrice(price) {
      return price != null ? parseFloat(price).toFixed(2) : '0.00';
    },

    async fetchAddons() {
      try {
        this.loading = true;
        const response = await ownerAddonApi.list();
        this.addons = response.data.data;
      } catch (error) {
        console.error('Failed to fetch addons:', error);
      } finally {
        this.loading = false;
      }
    },

    async toggleAvailability(addon) {
      const next = !addon.is_available;
      try {
        this.togglingId = addon.id;
        await ownerAddonApi.setAvailability(addon.id, next);
        addon.is_available = next; // optimistic local update
      } catch (error) {
        console.error('Failed to toggle availability:', error);
        alert(error.response?.data?.message || 'Failed to update availability');
      } finally {
        this.togglingId = null;
      }
    },

    confirmDelete(addon) {
      this.selectedAddon = addon;
      this.showDeleteModal = true;
    },

    async deleteAddon() {
      if (!this.selectedAddon) return;
      try {
        this.deleting = true;
        await ownerAddonApi.delete(this.selectedAddon.id);
        this.showDeleteModal = false;
        this.fetchAddons();
      } catch (error) {
        console.error('Failed to delete addon:', error);
        alert(error.response?.data?.message || 'Failed to delete add-on');
      } finally {
        this.deleting = false;
        this.selectedAddon = null;
      }
    }
  }
};
</script>

<style scoped>
.gl-addons-page { padding-bottom: 48px; }

/* ── Header ── */
.gl-branch-header { display: flex; align-items: flex-end; justify-content: space-between; padding: 28px 0 20px; }
.gl-branch-breadcrumb { display: flex; align-items: center; gap: 6px; font-size: 12.5px; color: #9ca3af; margin-bottom: 4px; }
.gl-branch-breadcrumb a { color: #9ca3af; text-decoration: none; }
.gl-branch-breadcrumb a:hover { color: #900C27; }
.gl-bc-sep { opacity: 0.5; }
.gl-branch-title { font-size: 22px; font-weight: 700; color: #111318; letter-spacing: -0.4px; margin: 0; }
.gl-branch-sub { font-size: 12.5px; color: #9ca3af; margin: 4px 0 0; max-width: 520px; }
.gl-add-btn {
  display: inline-flex; align-items: center; gap: 7px; padding: 9px 18px; border-radius: 10px;
  background: #900C27; color: #fff; font-size: 13.5px; font-weight: 600; text-decoration: none;
  transition: background 0.15s, transform 0.1s, box-shadow 0.15s; box-shadow: 0 2px 8px rgba(144,12,39,0.25);
}
.gl-add-btn:hover { background: #6f0a1e; color: #fff; transform: translateY(-1px); box-shadow: 0 4px 14px rgba(144,12,39,0.30); }

/* ── Stats strip ── */
.gl-stats-strip {
  display: flex; align-items: center; gap: 14px; background: #fff; border: 1.5px solid #f0f1f3;
  border-radius: 14px; padding: 12px 18px; margin-bottom: 16px;
}
.gl-stat-chip { display: flex; flex-direction: column; align-items: center; gap: 1px; min-width: 56px; }
.gl-stat-num { font-size: 18px; font-weight: 700; color: #111318; line-height: 1.1; }
.gl-stat-num.gl-stat-active { color: #900C27; }
.gl-stat-num.gl-stat-inactive { color: #ef4444; }
.gl-stat-lbl { font-size: 10.5px; color: #9ca3af; font-weight: 500; letter-spacing: 0.3px; }
.gl-stat-divider { width: 1px; height: 28px; background: #f0f1f3; flex-shrink: 0; }
.gl-stat-spacer { flex: 1; }
.gl-search-wrap { display: flex; align-items: center; gap: 7px; background: #f3f4f6; border-radius: 9px; padding: 7px 12px; min-width: 220px; }
.gl-search-wrap svg { color: #9ca3af; flex-shrink: 0; }
.gl-search-input { border: none; background: transparent; outline: none; font-size: 13px; color: #374151; width: 100%; }
.gl-search-input::placeholder { color: #9ca3af; }

/* ── Empty ── */
.gl-empty {
  display: flex; flex-direction: column; align-items: center; gap: 12px; padding: 64px 24px; color: #9ca3af;
  background: #fff; border: 1.5px solid #f0f1f3; border-radius: 16px;
}
.gl-empty svg { color: #d1d5db; }
.gl-empty p { font-size: 14px; margin: 0; }

/* ── Table ── */
.gl-table-wrap { background: #fff; border: 1.5px solid #f0f1f3; border-radius: 16px; overflow: hidden; margin-bottom: 20px; }
.gl-table { width: 100%; border-collapse: collapse; }
.gl-table thead tr { border-bottom: 1.5px solid #f0f1f3; }
.gl-table th {
  padding: 12px 16px; font-size: 11px; font-weight: 700; color: #9ca3af;
  text-transform: uppercase; letter-spacing: 0.6px; text-align: left; white-space: nowrap;
}
.gl-table th.text-center { text-align: center; }
.gl-table th.text-right { text-align: right; }
.gl-tbl-row { border-bottom: 1px solid #f8f9fa; transition: background 0.12s; }
.gl-tbl-row:last-child { border-bottom: none; }
.gl-tbl-row:hover { background: #fafef9; }
.gl-table td { padding: 13px 16px; vertical-align: middle; }
.gl-table td.text-center { text-align: center; }
.gl-table td.text-right { text-align: right; }

.gl-tbl-addon-name { font-size: 13.5px; font-weight: 600; color: #111318; }
.gl-sku { font-size: 12px; font-weight: 600; color: #6b7280; background: #f3f4f6; padding: 3px 9px; border-radius: 6px; font-family: ui-monospace, monospace; }
.gl-muted { color: #c4c8d0; }
.gl-group-pill { font-size: 11.5px; font-weight: 600; color: #900C27; background: rgba(144,12,39,0.10); padding: 3px 10px; border-radius: 20px; }
.gl-price { font-size: 13px; font-weight: 700; color: #111318; }
.gl-used-count { display: inline-block; min-width: 28px; font-size: 12px; font-weight: 700; color: #6b7280; background: #f3f4f6; padding: 3px 10px; border-radius: 20px; }

/* Availability toggle */
.gl-toggle {
  position: relative; width: 40px; height: 22px; border-radius: 20px; border: none;
  background: #d1d5db; cursor: pointer; transition: background 0.18s; padding: 0; vertical-align: middle;
}
.gl-toggle.on { background: #900C27; }
.gl-toggle:disabled { opacity: 0.6; cursor: wait; }
.gl-toggle-knob {
  position: absolute; top: 2px; left: 2px; width: 18px; height: 18px; border-radius: 50%;
  background: #fff; transition: transform 0.18s; box-shadow: 0 1px 3px rgba(0,0,0,0.2);
}
.gl-toggle.on .gl-toggle-knob { transform: translateX(18px); }

.gl-tbl-actions { display: flex; align-items: center; justify-content: center; gap: 6px; }
.gl-tbl-btn-edit, .gl-tbl-btn-delete {
  width: 32px; height: 32px; border-radius: 8px; border: none;
  display: flex; align-items: center; justify-content: center; cursor: pointer; text-decoration: none; transition: all 0.15s;
}
.gl-tbl-btn-edit { background: rgba(144,12,39,0.08); color: #900C27; }
.gl-tbl-btn-edit:hover { background: rgba(144,12,39,0.16); }
.gl-tbl-btn-delete { background: rgba(239,68,68,0.07); color: #ef4444; }
.gl-tbl-btn-delete:hover { background: rgba(239,68,68,0.14); }

/* ── Skeleton ── */
.gl-skel-row { display: flex; align-items: center; gap: 24px; padding: 16px; border-bottom: 1px solid #f8f9fa; }
.gl-skel-line { height: 12px; border-radius: 6px; background: linear-gradient(90deg, #f0f1f3 25%, #e8e9eb 50%, #f0f1f3 75%); background-size: 200% 100%; animation: gl-skel-wave 1.5s ease infinite; }
.gl-skel-line.w30 { width: 30%; } .gl-skel-line.w15 { width: 15%; } .gl-skel-line.w10 { width: 10%; }
@keyframes gl-skel-wave { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }

/* ── Mobile FAB ── */
.gl-mobile-fab { display: none; position: fixed; bottom: 24px; right: 20px; z-index: 200; text-decoration: none; }
.gl-fab-inner {
  display: inline-flex; align-items: center; gap: 8px; padding: 13px 20px; border-radius: 50px;
  background: #900C27; color: #fff; font-size: 14px; font-weight: 700;
  box-shadow: 0 6px 24px rgba(144,12,39,0.40), 0 2px 6px rgba(0,0,0,0.15); transition: transform 0.15s, box-shadow 0.15s;
}
.gl-mobile-fab:hover .gl-fab-inner { transform: translateY(-2px); box-shadow: 0 10px 30px rgba(144,12,39,0.45); }

/* ── Delete Modal ── */
.gl-modal-backdrop {
  position: fixed; inset: 0; background: rgba(0,0,0,0.35); display: flex; align-items: center; justify-content: center;
  z-index: 9999; backdrop-filter: blur(4px); animation: gl-backdrop-in 0.15s ease;
}
@keyframes gl-backdrop-in { from { opacity: 0; } to { opacity: 1; } }
.gl-modal {
  background: #fff; border-radius: 20px; padding: 32px 28px; width: 100%; max-width: 400px; margin: 16px; text-align: center;
  animation: gl-modal-in 0.2s cubic-bezier(0.34,1.56,0.64,1); box-shadow: 0 20px 60px rgba(0,0,0,0.15);
}
@keyframes gl-modal-in { from { opacity: 0; transform: scale(0.92) translateY(8px); } to { opacity: 1; transform: scale(1) translateY(0); } }
.gl-modal-icon { width: 52px; height: 52px; border-radius: 16px; margin: 0 auto 16px; display: flex; align-items: center; justify-content: center; }
.gl-modal-icon-danger { background: rgba(239,68,68,0.10); color: #ef4444; }
.gl-modal-title { font-size: 17px; font-weight: 700; color: #111318; margin: 0 0 8px; }
.gl-modal-body { font-size: 13.5px; color: #6b7280; margin: 0 0 16px; line-height: 1.5; }
.gl-modal-body strong { color: #111318; }
.gl-modal-blocked {
  display: flex; align-items: flex-start; gap: 8px; text-align: left; background: #fff8ed; border: 1px solid #fde68a;
  border-radius: 10px; padding: 12px 14px; margin-bottom: 20px; font-size: 13px; color: #92400e; line-height: 1.5;
}
.gl-modal-blocked svg { flex-shrink: 0; margin-top: 1px; color: #f59e0b; }
.gl-modal-blocked strong { color: #92400e; }
.gl-modal-actions { display: flex; gap: 10px; }
.gl-modal-cancel {
  flex: 1; height: 40px; border-radius: 10px; border: 1.5px solid #e5e7eb; background: #fff;
  color: #6b7280; font-size: 13.5px; font-weight: 600; cursor: pointer; transition: all 0.15s;
}
.gl-modal-cancel:hover { background: #f9fafb; border-color: #d1d5db; color: #374151; }
.gl-modal-confirm {
  flex: 1; height: 40px; border-radius: 10px; border: none; background: #ef4444; color: #fff;
  font-size: 13.5px; font-weight: 600; cursor: pointer; display: flex; align-items: center; justify-content: center; gap: 6px; transition: background 0.15s;
}
.gl-modal-confirm:hover:not(:disabled) { background: #dc2626; }
.gl-modal-confirm:disabled { opacity: 0.65; cursor: not-allowed; }

/* ── Mobile responsive ── */
@media (max-width: 767px) {
  .gl-addons-page { padding-bottom: 100px; }
  .gl-branch-header { padding: 14px 0 12px; }
  .gl-branch-title { font-size: 18px; }
  .gl-header-add-btn { display: none !important; }
  .gl-mobile-fab { display: flex; }
  .gl-stats-strip { flex-wrap: wrap; gap: 10px; padding: 10px 14px; }
  .gl-search-wrap { min-width: 100%; order: 3; }
  .gl-table-wrap { overflow-x: auto; }
  .gl-table { min-width: 640px; }
  .gl-modal-backdrop { padding: 68px 16px 16px !important; }
}
</style>

<style>
/* ── Dark mode ── */
body.dark-only .gl-branch-title { color: rgba(255,255,255,0.92) !important; }
body.dark-only .gl-branch-breadcrumb, body.dark-only .gl-branch-breadcrumb a, body.dark-only .gl-branch-sub { color: rgba(255,255,255,0.3) !important; }
body.dark-only .gl-stats-strip { background: #262932 !important; border-color: #323644 !important; }
body.dark-only .gl-stat-num { color: rgba(255,255,255,0.87) !important; }
body.dark-only .gl-stat-lbl { color: rgba(255,255,255,0.3) !important; }
body.dark-only .gl-stat-divider { background: #3d4255 !important; }
body.dark-only .gl-search-wrap { background: #323644 !important; }
body.dark-only .gl-search-input { color: rgba(255,255,255,0.8) !important; }
body.dark-only .gl-table-wrap { background: #262932 !important; border-color: #323644 !important; }
body.dark-only .gl-table th { color: rgba(255,255,255,0.3) !important; }
body.dark-only .gl-table thead tr { border-bottom-color: #323644 !important; }
body.dark-only .gl-tbl-row { border-bottom-color: #2d3044 !important; }
body.dark-only .gl-tbl-row:hover { background: rgba(144,12,39,0.06) !important; }
body.dark-only .gl-tbl-addon-name { color: rgba(255,255,255,0.87) !important; }
body.dark-only .gl-price { color: rgba(255,255,255,0.87) !important; }
body.dark-only .gl-sku, body.dark-only .gl-used-count { background: rgba(255,255,255,0.06) !important; color: rgba(255,255,255,0.55) !important; }
body.dark-only .gl-toggle { background: #3d4255 !important; }
body.dark-only .gl-toggle.on { background: #900C27 !important; }
body.dark-only .gl-tbl-btn-edit { background: rgba(144,12,39,0.12) !important; }
body.dark-only .gl-tbl-btn-delete { background: rgba(239,68,68,0.10) !important; }
body.dark-only .gl-empty { background: #262932 !important; border-color: #323644 !important; }
body.dark-only .gl-empty p { color: rgba(255,255,255,0.3) !important; }
body.dark-only .gl-skel-line { background: linear-gradient(90deg, #2d3044 25%, #323644 50%, #2d3044 75%) !important; background-size: 200% 100% !important; }
body.dark-only .gl-modal { background: #262932 !important; }
body.dark-only .gl-modal-title { color: rgba(255,255,255,0.92) !important; }
body.dark-only .gl-modal-body { color: rgba(255,255,255,0.5) !important; }
body.dark-only .gl-modal-body strong { color: rgba(255,255,255,0.87) !important; }
body.dark-only .gl-modal-cancel { background: #323644 !important; border-color: #3d4255 !important; color: rgba(255,255,255,0.55) !important; }
</style>
